import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { theme, type as text, floatingSheet, btnPrimary, btnSecondary, btnDanger, inputStyle } from "../theme";
import { api, apiBlob, apiErrorMessage } from "../api/client";
import { safeHref } from "../utils/safeHref";

// Platform Admin → Standards Watch → "Converted to markdown — review". The worker
// fetched an openly licensed instrument's official text from the catalogue's URL,
// converted it to markdown and compared it with the text the platform applied last
// (services/standardsRefresh.ts). This sheet shows what changed, how every seeded
// obligation stands against the provisions it cites, and the model's proposal for
// those rows — each accepted or not, row by row — then applies or discards it.
// Everything from the fetched page is rendered as text; the only links are the
// catalogue's own source URLs.

export type RefreshStatus = "converting" | "ready" | "unchanged" | "failed" | "applied" | "discarded" | "superseded";

interface SectionChange { title: string; level: number; added: string[]; removed: string[] }
interface RefreshDiff {
  baseline: "none" | "applied";
  charsBefore: number | null;
  charsAfter: number;
  sectionsBefore: number | null;
  sectionsAfter: number;
  added: Array<{ title: string; level: number; excerpt: string }>;
  removed: Array<{ title: string; level: number }>;
  changed: SectionChange[];
  unchanged: number;
  outline: Array<{ title: string; level: number }>;
  truncated: boolean;
}
type Standing = "unreferenced" | "matched" | "changed" | "partly_missing" | "missing";
interface ObligationMapping { controlId: string; name: string; refs: string[]; found: string[]; missing: string[]; changed: string[]; standing: Standing }
interface ProposedUpdate { controlId: string; name?: string; description?: string; requiredEvidence?: string[]; reason: string }
interface ProposedAddition { id: string; domain: string; name: string; description: string; requiredEvidence: string[]; provision: string | null; reason: string }
interface RefreshProposal { updates: ProposedUpdate[]; additions: ProposedAddition[]; removals: Array<{ controlId: string; reason: string }>; dropped: string[] }

interface RefreshDetail {
  id: string;
  entryKey: string;
  entryLabel: string;
  implemented: string | null;
  status: RefreshStatus;
  licence: string;
  sourceUrls: string[];
  charCount: number | null;
  anchored: boolean;
  truncated: boolean;
  diff: RefreshDiff | null;
  mapping: ObligationMapping[] | null;
  proposal: RefreshProposal | null;
  proposalNote: string | null;
  suggestedVersion: string | null;
  error: string | null;
  convertedAt: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  autoApplied: boolean;
  preview: string | null;
  previewTruncated: boolean;
  currentRows: Array<{ id: string; name: string; description: string; requiredEvidence: string[] }>;
  appliedText: { id: string; version: string; appliedAt: string; charCount: number } | null;
  autoApply: boolean;
  autoApplyBlockers: string[];
}

const STANDING: Record<Standing, { label: string; color: string; bg: string; title: string }> = {
  matched:        { label: "Matches", color: theme.color.successText, bg: theme.color.successBg, title: "Every provision it cites is in the text and unchanged" },
  changed:        { label: "Provision changed", color: theme.color.warningText, bg: theme.color.warningBg, title: "A provision it cites reads differently from the applied text" },
  partly_missing: { label: "Partly missing", color: theme.color.dangerText, bg: theme.color.dangerBg, title: "Some of the provisions it cites are no longer in the text" },
  missing:        { label: "Provision missing", color: theme.color.dangerText, bg: theme.color.dangerBg, title: "None of the provisions it cites is in the text: repealed or renumbered?" },
  unreferenced:   { label: "Cites no provision", color: theme.color.muted, bg: theme.color.panelAlt, title: "Its name cites no article or section, so it cannot be matched automatically" },
};

function fmt(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function Tag({ label, color, bg, title }: { label: string; color: string; bg: string; title?: string }) {
  return (
    <span title={title} style={{ display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 2, color, background: bg, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

const sectionLabel: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.05em", margin: "18px 0 8px" };
const pre: React.CSSProperties = {
  margin: 0, padding: 10, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm,
  fontSize: 11.5, lineHeight: 1.5, color: theme.color.text, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 280, overflow: "auto",
};

async function download(path: string, filename: string) {
  const blob = await apiBlob(path);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function StandardsRefreshReview({ refreshId, onClose, onChanged }: { refreshId: string; onClose: () => void; onChanged: () => void }) {
  const [detail, setDetail] = useState<RefreshDetail | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [appliedVersion, setAppliedVersion] = useState("");
  const [note, setNote] = useState("");
  const [updates, setUpdates] = useState<Set<string>>(new Set());
  const [additions, setAdditions] = useState<Set<string>>(new Set());
  const [flags, setFlags] = useState<Set<string>>(new Set());
  const [openSections, setOpenSections] = useState<Set<number>>(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [showOutline, setShowOutline] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    api.get<{ refresh: RefreshDetail }>(`/admin/standards-watch/refreshes/${refreshId}`)
      .then(({ refresh }) => {
        setDetail(refresh);
        setAppliedVersion(refresh.suggestedVersion ?? "");
        // Proposals start accepted (the owner unticks what they disagree with); a provision that left
        // the text starts flagged, because a flag changes nothing but puts the row in front of a person.
        setUpdates(new Set((refresh.proposal?.updates ?? []).map((u) => u.controlId)));
        setAdditions(new Set((refresh.proposal?.additions ?? []).map((a) => a.id)));
        setFlags(new Set([
          ...(refresh.proposal?.removals ?? []).map((r) => r.controlId),
          ...(refresh.mapping ?? []).filter((m) => m.standing === "missing").map((m) => m.controlId),
        ]));
      })
      .catch((e) => setError(apiErrorMessage(e, "Could not load the refresh.")));
  }, [refreshId]);

  const currentById = useMemo(() => new Map((detail?.currentRows ?? []).map((r) => [r.id, r])), [detail]);
  const flaggable = useMemo(() => {
    const out = new Map<string, string>();
    for (const m of detail?.mapping ?? []) if (m.standing === "missing" || m.standing === "partly_missing") out.set(m.controlId, `Cites ${m.missing.join(", ")}, which the refreshed text no longer has`);
    for (const r of detail?.proposal?.removals ?? []) out.set(r.controlId, r.reason);
    return out;
  }, [detail]);

  const toggleSection = (i: number) => {
    const next = new Set(openSections);
    if (next.has(i)) next.delete(i); else next.add(i);
    setOpenSections(next);
  };
  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  async function apply() {
    if (!detail) return;
    setBusy(true);
    setError("");
    try {
      const { result } = await api.post<{ result: { updated: string[]; added: string[]; flagged: string[] } }>(`/admin/standards-watch/refreshes/${detail.id}/apply`, {
        appliedVersion: appliedVersion.trim(), note: note.trim() || undefined,
        acceptUpdates: [...updates], acceptAdditions: [...additions], acceptFlags: [...flags],
      });
      setDone(`Applied. ${result.updated.length} row(s) updated, ${result.added.length} added, ${result.flagged.length} flagged. The compliance check and policy generation read this text from now on.`);
      onChanged();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not apply the refresh."));
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    if (!detail) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/admin/standards-watch/refreshes/${detail.id}/discard`, { note: note.trim() || undefined });
      setDone("Discarded. Nothing the platform reads has changed.");
      onChanged();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not discard the refresh."));
    } finally {
      setBusy(false);
    }
  }

  const d = detail?.diff ?? null;
  const ready = detail?.status === "ready" && !done;

  return (
    <div role="dialog" aria-modal="true" aria-label="Review converted text" onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.55)", display: "flex", justifyContent: "center", alignItems: "flex-start", padding: "40px 16px", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...floatingSheet, width: "min(1040px, 100%)", padding: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 18px", borderBottom: `1px solid ${theme.color.line}` }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Converted to markdown — review</div>
            <div style={{ fontSize: 12, color: theme.color.muted }}>{detail?.entryLabel ?? "…"}</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, padding: 4 }}><X size={18} /></button>
        </div>

        <div style={{ padding: "14px 18px 18px", fontSize: 13, color: theme.color.text, lineHeight: 1.55 }}>
          {!detail && !error && <div style={{ color: theme.color.muted }}>Loading…</div>}
          {error && <div style={{ color: theme.color.dangerText, marginBottom: 10 }}>{error}</div>}
          {detail && (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 12, color: theme.color.muted }}>
                <span>Status: <strong style={{ color: theme.color.text }}>{detail.status}</strong>{detail.autoApplied ? " (auto-applied)" : ""}</span>
                <span>Converted {fmt(detail.convertedAt)}</span>
                {detail.charCount !== null && <span>{detail.charCount.toLocaleString("en-GB")} characters</span>}
                <span>Licence: {detail.licence}</span>
              </div>
              <div style={{ marginTop: 6, fontSize: 12 }}>
                {detail.sourceUrls.map((u) => {
                  const href = safeHref(u);
                  return <div key={u}>Fetched from {href ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: theme.color.ref, wordBreak: "break-all" }}>{u}</a> : u}</div>;
                })}
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: theme.color.muted }}>
                {detail.appliedText
                  ? <>Compared with the text in force: {detail.appliedText.version} (applied {fmt(detail.appliedText.appliedAt)}).</>
                  : <>Nothing has been applied for this instrument before: applying makes this the first official text the platform reads for it, beside the seeded summaries ({detail.implemented ?? "—"}).</>}
              </div>
              {!detail.anchored && <div style={{ marginTop: 8, color: theme.color.warningText }}>A region anchor was not found on the page, so the whole page was kept: check the start and end of the text before applying.</div>}
              {detail.truncated && <div style={{ marginTop: 8, color: theme.color.warningText }}>The text was cut at the size limit: the end of the instrument is missing.</div>}
              {detail.error && <div style={{ marginTop: 8, color: theme.color.dangerText }}>{detail.error}</div>}
              {detail.decisionNote && <div style={{ marginTop: 8, color: theme.color.muted }}>Note: {detail.decisionNote}</div>}

              {d && (
                <>
                  <div style={sectionLabel}>What changed in the text</div>
                  {d.baseline === "none" ? (
                    <div>
                      {d.sectionsAfter} sections, {d.charsAfter.toLocaleString("en-GB")} characters.{" "}
                      <button onClick={() => setShowOutline((v) => !v)} style={{ background: "none", border: "none", padding: 0, color: theme.color.accentText, cursor: "pointer", fontSize: 13 }}>
                        {showOutline ? "Hide the outline" : "Show the outline"}
                      </button>
                      {showOutline && (
                        <div style={{ ...pre, marginTop: 8 }}>
                          {d.outline.map((h, i) => <div key={i} style={{ paddingLeft: Math.max(0, h.level - 1) * 14 }}>{h.title}</div>)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div style={{ marginBottom: 8 }}>
                        {d.changed.length} section(s) changed, {d.added.length} new, {d.removed.length} no longer present, {d.unchanged} unchanged
                        {d.charsBefore !== null ? ` · ${d.charsBefore.toLocaleString("en-GB")} → ${d.charsAfter.toLocaleString("en-GB")} characters` : ""}
                        {d.truncated ? " · lists cut for length" : ""}
                      </div>
                      {d.changed.map((s, i) => (
                        <div key={`c${i}`} style={{ borderTop: `1px solid ${theme.color.border}`, padding: "6px 0" }}>
                          <button onClick={() => toggleSection(i)}
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: theme.color.heading, fontSize: 13, fontWeight: 600, textAlign: "left" }}>
                            {openSections.has(i) ? "▾" : "▸"} {s.title} <span style={{ color: theme.color.muted, fontWeight: 400 }}>(+{s.added.length} / −{s.removed.length})</span>
                          </button>
                          {openSections.has(i) && (
                            <div style={{ ...pre, marginTop: 6 }}>
                              {s.added.map((x, j) => <div key={`a${j}`} style={{ color: theme.color.successText }}>+ {x}</div>)}
                              {s.removed.map((x, j) => <div key={`r${j}`} style={{ color: theme.color.dangerText }}>− {x}</div>)}
                            </div>
                          )}
                        </div>
                      ))}
                      {d.added.map((s, i) => (
                        <div key={`n${i}`} style={{ borderTop: `1px solid ${theme.color.border}`, padding: "6px 0" }}>
                          <div style={{ fontWeight: 600, color: theme.color.heading }}>New: {s.title}</div>
                          {s.excerpt && <div style={{ fontSize: 12, color: theme.color.muted }}>{s.excerpt}</div>}
                        </div>
                      ))}
                      {d.removed.map((s, i) => (
                        <div key={`x${i}`} style={{ borderTop: `1px solid ${theme.color.border}`, padding: "6px 0", color: theme.color.dangerText }}>No longer present: {s.title}</div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {(detail.mapping?.length ?? 0) > 0 && (
                <>
                  <div style={sectionLabel}>The seeded obligations against the text</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${theme.color.line}`, color: theme.color.muted, textAlign: "left" }}>
                        <th style={{ padding: "4px 6px" }}>Row</th><th style={{ padding: "4px 6px" }}>Cites</th><th style={{ padding: "4px 6px" }}>Standing</th><th style={{ padding: "4px 6px" }}>Flag for review</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.mapping!.map((m) => {
                        const s = STANDING[m.standing];
                        return (
                          <tr key={m.controlId} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                            <td style={{ padding: "5px 6px" }}><div style={{ ...text.mono, fontSize: 11, color: theme.color.muted }}>{m.controlId}</div>{m.name}</td>
                            <td style={{ padding: "5px 6px" }}>{m.refs.join(", ") || "—"}{m.missing.length > 0 && <div style={{ color: theme.color.dangerText }}>not found: {m.missing.join(", ")}</div>}{m.changed.length > 0 && <div style={{ color: theme.color.warningText }}>changed: {m.changed.join(", ")}</div>}</td>
                            <td style={{ padding: "5px 6px" }}><Tag {...s} /></td>
                            <td style={{ padding: "5px 6px" }}>
                              {flaggable.has(m.controlId) && (
                                <input type="checkbox" disabled={!ready} checked={flags.has(m.controlId)} onChange={() => toggle(flags, setFlags, m.controlId)} aria-label={`Flag ${m.controlId} for review`} />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>A flag puts the row in front of a person on this page; the row and every organisation's assessment of it stay.</div>
                </>
              )}

              <div style={sectionLabel}>Proposed changes to the seeded rows</div>
              {detail.proposalNote && <div style={{ color: theme.color.muted, marginBottom: 6 }}>{detail.proposalNote}</div>}
              {detail.proposal && (
                <>
                  {detail.proposal.updates.map((u) => {
                    const cur = currentById.get(u.controlId);
                    return (
                      <div key={u.controlId} style={{ border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm, padding: 10, marginBottom: 8 }}>
                        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: ready ? "pointer" : "default" }}>
                          <input type="checkbox" disabled={!ready} checked={updates.has(u.controlId)} onChange={() => toggle(updates, setUpdates, u.controlId)} />
                          <span><strong>Update {u.controlId}</strong> — <span style={{ color: theme.color.muted }}>{u.reason}</span></span>
                        </label>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8, fontSize: 12 }}>
                          <div><div style={{ color: theme.color.faint, fontSize: 11 }}>NOW</div>{cur ? <><div style={{ fontWeight: 600 }}>{cur.name}</div><div>{cur.description}</div><div style={{ color: theme.color.muted }}>Evidence: {cur.requiredEvidence.join("; ")}</div></> : "—"}</div>
                          <div><div style={{ color: theme.color.faint, fontSize: 11 }}>PROPOSED</div><div style={{ fontWeight: 600 }}>{u.name ?? cur?.name}</div><div>{u.description ?? cur?.description}</div><div style={{ color: theme.color.muted }}>Evidence: {(u.requiredEvidence ?? cur?.requiredEvidence ?? []).join("; ")}</div></div>
                        </div>
                      </div>
                    );
                  })}
                  {detail.proposal.additions.map((a) => (
                    <div key={a.id} style={{ border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.sm, padding: 10, marginBottom: 8 }}>
                      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: ready ? "pointer" : "default" }}>
                        <input type="checkbox" disabled={!ready} checked={additions.has(a.id)} onChange={() => toggle(additions, setAdditions, a.id)} />
                        <span><strong>Add {a.id}</strong> ({a.domain}{a.provision ? `, ${a.provision}` : ""}) — <span style={{ color: theme.color.muted }}>{a.reason}</span></span>
                      </label>
                      <div style={{ marginTop: 6, fontSize: 12 }}><div style={{ fontWeight: 600 }}>{a.name}</div><div>{a.description}</div><div style={{ color: theme.color.muted }}>Evidence: {a.requiredEvidence.join("; ")}</div></div>
                    </div>
                  ))}
                  {detail.proposal.removals.length > 0 && (
                    <div style={{ fontSize: 12, color: theme.color.muted, marginBottom: 6 }}>
                      The model suggests {detail.proposal.removals.length} row(s) no longer apply; they are listed with a flag in the table above and are never deleted.
                    </div>
                  )}
                  {detail.proposal.dropped.length > 0 && (
                    <div style={{ fontSize: 11, color: theme.color.faint }}>Left out by validation: {detail.proposal.dropped.join("; ")}</div>
                  )}
                  {!detail.proposal.updates.length && !detail.proposal.additions.length && !detail.proposal.removals.length && <div style={{ color: theme.color.muted }}>No change proposed.</div>}
                </>
              )}

              <div style={sectionLabel}>The converted text</div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                {detail.preview && (
                  <button onClick={() => setShowPreview((v) => !v)} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }}>{showPreview ? "Hide the text" : "Show the text"}</button>
                )}
                {detail.preview && (
                  <button onClick={() => download(`/admin/standards-watch/refreshes/${detail.id}/markdown`, `${detail.entryKey}-converted.md`).catch((e) => setError(apiErrorMessage(e, "Could not download the text.")))} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }}>
                    Download markdown
                  </button>
                )}
                {!detail.preview && <span style={{ color: theme.color.muted }}>No text is kept for this refresh.</span>}
              </div>
              {showPreview && detail.preview && (
                <pre style={{ ...pre, marginTop: 8, maxHeight: 420 }}>{detail.preview}{detail.previewTruncated ? "\n\n… (the rest is in the download)" : ""}</pre>
              )}

              {detail.autoApply && detail.autoApplyBlockers.length > 0 && detail.status === "ready" && (
                <div style={{ marginTop: 14, fontSize: 12, color: theme.color.warningText }}>Auto-apply is on for this instrument but held this one back: {detail.autoApplyBlockers.join("; ")}.</div>
              )}

              {done && <div style={{ marginTop: 16, color: theme.color.successText }}>{done}</div>}

              {ready && (
                <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${theme.color.line}`, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div style={{ flex: 3, minWidth: 260 }}>
                    <label style={{ fontSize: 11, color: theme.color.muted, display: "block", marginBottom: 4 }}>What the platform now encodes (required)</label>
                    <input value={appliedVersion} onChange={(e) => setAppliedVersion(e.target.value)} maxLength={300} style={inputStyle} />
                  </div>
                  <div style={{ flex: 2, minWidth: 200 }}>
                    <label style={{ fontSize: 11, color: theme.color.muted, display: "block", marginBottom: 4 }}>Note (optional)</label>
                    <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} style={inputStyle} />
                  </div>
                  <button disabled={busy || appliedVersion.trim().length < 3} onClick={apply} style={{ ...btnPrimary, padding: "8px 16px", fontSize: 12 }}>
                    {busy ? "Applying…" : `Apply refresh${updates.size + additions.size + flags.size ? ` (${updates.size + additions.size + flags.size} row change${updates.size + additions.size + flags.size === 1 ? "" : "s"})` : ""}`}
                  </button>
                  <button disabled={busy} onClick={discard} style={{ ...btnDanger, padding: "8px 16px", fontSize: 12 }}>Discard</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
